import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  Link,
  CircularProgress,
  Alert
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { companyService } from '../services/companyService';

const CompanySelector = ({ value, onChange, error, helperText }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    // Find and set the selected company when value changes
    if (value && companies.length > 0) {
      const company = companies.find(c => c.companyId === value);
      setSelectedCompany(company || null);
    } else {
      setSelectedCompany(null);
    }
  }, [value, companies]);

  const loadCompanies = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const companiesData = await companyService.getCompanies();
      setCompanies(companiesData);
    } catch (err) {
      console.error('Error loading companies:', err);
      setLoadError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (event, newValue) => {
    setSelectedCompany(newValue);
    onChange(newValue ? newValue.companyId : '');
  };

  const handleCompanyClick = (companyId) => {
    if (companyId) {
      window.open(`/company/${companyId}`, '_blank');
    }
  };

  return (
    <Box>
      <Autocomplete
        value={selectedCompany}
        onChange={handleCompanyChange}
        options={companies}
        getOptionLabel={(option) => option?.companyName || ''}
        isOptionEqualToValue={(option, value) => option?.companyId === value?.companyId}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Company"
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body1">{option.companyName}</Typography>
              {option.mainPhone && (
                <Typography variant="body2" color="text.secondary">
                  📞 {option.mainPhone}
                </Typography>
              )}
              {option.email && (
                <Typography variant="body2" color="text.secondary">
                  ✉️ {option.email}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        noOptionsText={loading ? "Loading companies..." : "No companies found"}
        filterOptions={(options, { inputValue }) => {
          if (!inputValue) return options;
          return options.filter(option =>
            option?.companyName?.toLowerCase().includes(inputValue.toLowerCase())
          );
        }}
      />

      {/* Show selected company as a clickable link */}
      {selectedCompany && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Selected: 
            <Link
              component="button"
              variant="body2"
              onClick={() => handleCompanyClick(selectedCompany.companyId)}
              sx={{ ml: 1, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {selectedCompany.companyName}
            </Link>
          </Typography>
        </Box>
      )}

      {loadError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {loadError}
        </Alert>
      )}
    </Box>
  );
};

export default CompanySelector; 